import { FC, useCallback, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getBusinessClientObject } from 'store/authentication/selectors';
import { ChatBotV2 } from 'ts-components/ChatBotV2';
import { AIChatAPI, PreviewItem } from 'ts-components/ChatBotV2/types';
import { SESSION_STORAGE_KEY } from 'ts-components/ChatBotV2/constants';
import { QuickResponseData } from 'ts-components/QuickResponses';
import { useToast, toastVariant } from '__components/ToastProvider';
import { useSessionStorage } from 'hooks/useSessionStorage';
import { monitoringMessageHelper } from 'instrumentation/helpers';
import { PageWrapper } from 'pages/UnifiedChatEmbed/styles';
import { useKizenAiFrameMessage } from './useKizenAiFrameMessage';
import {
  MENU_OPTION,
  DEFAULT_GRAPH,
  DEFAULT_TAG,
  QUERY_PARAM,
  FRAME_ACTION,
  POST_MESSAGE_SOURCE,
  POST_MESSAGE_EVENT,
  AI_SAMPLE_IDEAS,
} from './constants';

const AIChatEmbed: FC = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const [showToast] = useToast();
  const chatAPIRef = useRef<AIChatAPI | null>(null);
  const { removeItem } = useSessionStorage();

  const queryParams = new URLSearchParams(location.search);
  const urlTag = queryParams.get(QUERY_PARAM.TAG) || DEFAULT_TAG;
  const headerLogoUrl = queryParams.get(QUERY_PARAM.HEADER_LOGO_URL);
  const headerText = queryParams.get(QUERY_PARAM.HEADER) || undefined;

  const [chatBotV2Key, setChatBotV2Key] = useState(0);
  const [currentGraph, setCurrentGraph] = useState(DEFAULT_GRAPH);
  const [currentTag, setCurrentTag] = useState(urlTag);
  const pendingAIChatMessageRef = useRef('');

  const business = useSelector(
    (s: { authentication: { chosenBusiness: any } }) =>
      s.authentication.chosenBusiness
  );
  const businessClientObject = useSelector(getBusinessClientObject);
  const { ai_preview: hasAiGraphPreview = false } =
    business?.entitlements || {};

  // Update graph/tag and remount ChatBotV2 when sendUserMessage includes a graphName
  useKizenAiFrameMessage(
    (data) => {
      if (data.action === FRAME_ACTION.SEND_USER_MESSAGE && data.graphName) {
        const graphName = data.graphName as string;
        const tag = (data.graphTag as string | undefined) || urlTag;
        setCurrentGraph(graphName);
        setCurrentTag(tag);
        pendingAIChatMessageRef.current = (data.message as string) || '';
        removeItem(SESSION_STORAGE_KEY);
        setChatBotV2Key((k) => k + 1);
      }
    },
    { iframeOnly: true }
  );

  // Keep currentGraph/currentTag in sync with changeGraphConfig
  useKizenAiFrameMessage(
    (data) => {
      if (data.action === FRAME_ACTION.CHANGE_GRAPH_CONFIG && data.graphName) {
        const graphName = data.graphName as string;
        const tag = (data.graphTag as string | undefined) || urlTag;
        setCurrentGraph(graphName);
        setCurrentTag(tag);
      }
    },
    { iframeOnly: true }
  );

  const dynamicHeader = useMemo(() => {
    return headerText || t('Kizen AI (Beta)');
  }, [headerText, t]);

  const headerMenuOptions = useMemo(() => {
    const options = [];

    if (hasAiGraphPreview) {
      options.push({
        value: MENU_OPTION.CHANGE_MODEL,
        label: t('Change Model'),
      });
    }

    options.push({ value: MENU_OPTION.NEW_CHAT, label: t('New Chat') });
    options.push({ value: MENU_OPTION.CLOSE, label: t('Hide') });

    return options;
  }, [t, hasAiGraphPreview]);

  const handleHeaderMenuOptionSelect = useCallback(
    ({ value }: { value: string }) => {
      switch (value) {
        case MENU_OPTION.CHANGE_MODEL:
          removeItem(SESSION_STORAGE_KEY);
          window.parent?.postMessage(
            {
              source: POST_MESSAGE_SOURCE,
              event: { type: POST_MESSAGE_EVENT.OPEN_GRAPH_MODEL_CONFIG },
            },
            '*'
          );
          break;
        case MENU_OPTION.NEW_CHAT:
          chatAPIRef.current?.resetChat();
          break;
        case MENU_OPTION.CLOSE:
          window.parent?.postMessage(
            {
              source: POST_MESSAGE_SOURCE,
              event: { type: POST_MESSAGE_EVENT.CLOSE_AI_CHAT },
            },
            '*'
          );
          break;
      }
    },
    [removeItem]
  );

  const handleActionSelect = useCallback(
    (value: string) => {
      handleHeaderMenuOptionSelect({ value });
    },
    [handleHeaderMenuOptionSelect]
  );

  const handleReady = useCallback((api: AIChatAPI) => {
    chatAPIRef.current = api;
  }, []);

  const handleError = useCallback(
    (error: Error) => {
      showToast({
        message: error.message || t('Something went wrong. Please try again.'),
        variant: toastVariant.FAILURE,
      });
    },
    [showToast, t]
  );

  const handleCopyMessage = useCallback(async (messageContent: string) => {
    if (window.self === window.top) {
      try {
        await navigator.clipboard.writeText(messageContent);
      } catch (error) {
        monitoringMessageHelper('AIChatEmbed failed to copy message', {
          extra: { error },
        });
      }
    } else {
      window.parent?.postMessage(
        {
          source: POST_MESSAGE_SOURCE,
          event: { type: POST_MESSAGE_EVENT.COPY_TO_CLIPBOARD, messageContent },
        },
        '*'
      );
    }
  }, []);

  const handlePreviewNavigate = useCallback(
    (preview: PreviewItem) => {
      const { url } = preview;
      const context =
        preview.type === 'filter'
          ? {
              unsavedFilter: preview.data.filter_config,
              filterName: preview.data.filter_name,
            }
          : undefined;

      window.parent?.postMessage(
        {
          source: POST_MESSAGE_SOURCE,
          event: {
            type: POST_MESSAGE_EVENT.OPEN_WINDOW,
            url:
              businessClientObject?.id && url?.includes(businessClientObject.id)
                ? '/clients'
                : url,
            target: '_self',
            context,
          },
        },
        '*'
      );
    },
    [businessClientObject?.id]
  );

  const sampleIdeas: QuickResponseData = useMemo(() => {
    return {
      options: AI_SAMPLE_IDEAS(t).map((label: string) => ({
        label,
        value: label,
      })),
      selectType: 'single_select',
    };
  }, [t]);

  return (
    <PageWrapper data-qa-id="ai-chat-page">
      <ChatBotV2
        key={chatBotV2Key}
        config={{ graph: currentGraph, tag: currentTag }}
        initialMessage={pendingAIChatMessageRef.current || undefined}
        autoConnect
        header={dynamicHeader}
        showLogo
        headerLogoUrl={headerLogoUrl}
        sampleIdeas={sampleIdeas}
        enableQuickActionButtons
        onCreateNewChat={(context) => {
          const graphName = context?.graphName as string | undefined;
          const graphTag = context?.graphTag as string | undefined;
          if (graphName) {
            setCurrentGraph(graphName);
            setCurrentTag(graphTag || urlTag);
          }
        }}
        headerMenuOptions={headerMenuOptions}
        onHeaderMenuOptionSelect={handleHeaderMenuOptionSelect}
        onActionSelect={handleActionSelect}
        onReady={handleReady}
        onError={handleError}
        onCopyMessage={handleCopyMessage}
        onPreviewNavigate={handlePreviewNavigate}
        renderMarkdown
      />
    </PageWrapper>
  );
};

export default AIChatEmbed;
